// /login — magic-link request form.
//
// MUST show identical UI feedback regardless of whether the email is provisioned
// (email enumeration protection). Always: "If that address has access, a sign-in
// link is on its way." (per spec §2.3).

import { useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import brandmarkBlack from '../assets/solomon-brandmark-black.svg';

const REDIRECT_URL = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '';

export function LoginRoute(): React.ReactNode {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(ev: FormEvent<HTMLFormElement>): Promise<void> {
    ev.preventDefault();
    setPending(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: REDIRECT_URL, shouldCreateUser: false },
      });
      // We deliberately do NOT distinguish error vs success in the UI.
      if (error) {
        logger.warn({
          source: 'login',
          message: 'signInWithOtp returned error (UI hidden for enumeration protection)',
          payload: { code: error.code ?? null },
        });
      }
    } catch (err: unknown) {
      logger.error({
        source: 'login',
        message: 'signInWithOtp threw',
        payload: { error: String(err) },
      });
    } finally {
      setPending(false);
      setSubmitted(true);
    }
  }

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="flex min-h-screen items-center justify-center bg-cream px-6 py-12 focus:outline-none"
    >
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center">
          <img src={brandmarkBlack} alt="Solomon Advising" className="h-12 w-auto" />
        </div>
        <div className="rounded-md border border-border-light bg-white p-8 shadow-card">
          <h1 className="mb-2 text-[28px]">Sign in</h1>
          <p className="mb-6 text-text-body">
            Enter the email address Solomon has on file for you. We&rsquo;ll send a one-time sign-in
            link.
          </p>

          {submitted ? (
            <p role="status" aria-live="polite" className="rounded-sm bg-cream p-4 text-text-body">
              If that address has access, a sign-in link is on its way. Check your inbox (and spam
              folder, just in case).
            </p>
          ) : (
            <form onSubmit={(ev) => void onSubmit(ev)} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-text-dark">Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className="w-full rounded-sm border border-border-strong bg-white px-3 py-2 text-[15px] focus-visible:outline-2 focus-visible:outline-orange"
                  placeholder="you@yourcompany.com"
                />
              </label>
              <button
                type="submit"
                disabled={pending}
                className="inline-flex w-full items-center justify-center rounded-sm bg-orange px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-orange-deep disabled:opacity-60"
              >
                {pending ? 'Sending…' : 'Send me a magic link'}
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-[12px] text-text-muted">
          Need access? Email{' '}
          <a className="underline hover:text-orange" href="mailto:support@solomonadvising.com">
            support@solomonadvising.com
          </a>
          .
        </p>
        {/* Build SHA footer — issue #14 / ADR-032. Useful for support */}
        {/* triage ("what version are you running?") and as a visible drift */}
        {/* signal alongside the smoke harness assertion. */}
        <p
          className="mt-2 text-center font-mono text-[11px] text-text-muted"
          aria-label="Build version"
          data-testid="build-info-footer"
        >
          v: {__BUILD_SHA_SHORT__} — {__BUILD_TIME_ISO__}
          {__BUILD_DIRTY__ ? ' ⚠ dirty' : ''}
        </p>
      </div>
    </main>
  );
}
